import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { logout as logoutApi } from '../../utils/AuthService';
import { getFullUrl } from '../../utils/url';

interface RootState {
  user: {
    userId: string;
    fullName: string;
    title: string;
    role: string;
    imageUrl: string;
    companyName: string;
    companyPrimaryColor?: string;
  };
}

/**
 * User menu dropdown in TopBar.
 * Shows user avatar, name, role, and dropdown with profile, settings, logout.
 */
const UserMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  const { userId, fullName, title, role, imageUrl, companyName, companyPrimaryColor } = useSelector(
    (state: RootState) => state.user
  );

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    // Don't clear Redux/storage state here — the session is still valid on the backend.
    // signoutRedirect() navigates the browser to the backend logout endpoint, which
    // invalidates the session and then redirects back to /logoutcallback where
    // LogoutCallback clears the local state.
    await logoutApi();
  };

  // Generate initials from full name
  const initials = fullName
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  // Reset image error when URL changes (e.g. after INIT refreshes stale redux-persist data)
  useEffect(() => { setImgError(false); }, [imageUrl]);

  // Build avatar URL if image exists
  const avatarUrl = getFullUrl(imageUrl);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
        data-testid="user-menu-trigger"
        className="flex items-center space-x-2 md:space-x-3 rtl:space-x-reverse p-2 min-h-[44px] rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {/* Avatar */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
          style={{
            background: (avatarUrl && !imgError) ? 'transparent' : `linear-gradient(to bottom right, ${companyPrimaryColor || '#304EA1'}, #6366f1)`
          }}
        >
          {(avatarUrl && !imgError) ? (
            <img src={avatarUrl} alt={fullName} className="w-full h-full object-cover" onError={() => setImgError(true)} />
          ) : (
            <span className="text-white text-sm font-semibold">{initials}</span>
          )}
        </div>
        <div className="hidden md:block text-left rtl:text-right">
          <p className="text-sm font-medium text-foreground">{fullName}</p>
          <p className="text-xs text-muted-foreground">{title}</p>
        </div>
        <Icon name="ChevronDown" size={16} className="text-muted-foreground hidden md:block" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-64 bg-popover rounded-lg shadow-lg border border-border z-50">
          <div className="p-4 border-b border-border">
            <p className="text-sm font-semibold text-foreground">{fullName}</p>
            <p className="text-xs text-muted-foreground mt-1">{companyName ? `${companyName} · ${title}` : title}</p>
          </div>
          <div className="py-2">
            <button
              onClick={() => {
                navigate(`/profile/${userId}/personal`);
                setIsOpen(false);
              }}
              className="w-full flex items-center space-x-3 rtl:space-x-reverse px-4 py-2.5 min-h-[44px] text-sm text-foreground hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Icon name="User" size={16} />
              <span>{t('My Profile')}</span>
            </button>
            <button
              onClick={() => {
                navigate('/settings/notifications');
                setIsOpen(false);
              }}
              className="w-full flex items-center space-x-3 rtl:space-x-reverse px-4 py-2.5 min-h-[44px] text-sm text-foreground hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Icon name="Bell" size={16} />
              <span>{t('Notification Preferences')}</span>
            </button>
          </div>
          <div className="border-t border-border py-2">
            <button
              onClick={handleLogout}
              data-testid="user-menu-logout"
              className="w-full flex items-center space-x-3 rtl:space-x-reverse px-4 py-2.5 min-h-[44px] text-sm text-destructive hover:bg-destructive/10 transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Icon name="LogOut" size={16} />
              <span>{t('Logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
